import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useStepUpInvoke } from '@/components/auth/StepUpProvider';
import type { BillingInterval } from '@/lib/billingPlans';
import type { KnownCustomerFeatureKey } from '@/lib/customerFeaturesCatalog';
import {
  buildFunctionInvokeError,
  FunctionInvokeError,
  getFunctionInvokeErrorSentryContext,
  getFunctionInvokeErrorSentryFingerprint,
  getFunctionInvokeSentryTitle,
  isFunctionInvokeAbortError,
} from '@/lib/functionInvokeErrors';
import {
  StepUpCancelledError,
  StepUpFactorMissingError,
} from '@/lib/stepUpAal2';

interface SubscriptionInfo {
  subscribed: boolean;
  billingType: 'stripe' | 'manual' | 'grandfathered' | null;
  subscriptionStatus: string | null;
  planSlug: string | null;
  planName: string | null;
  billingInterval: 'monthly' | 'annual' | null;
  subscriptionEnd: string | null;
  canceledAt: string | null;
  commitmentStartedAt: string | null;
  commitmentEndsAt: string | null;
}

export interface CheckoutSessionInfo {
  url: string | null;
  clientSecret: string | null;
  sessionId: string | null;
  checkoutMode: 'redirect' | 'custom';
}

type BillingChangeInvoiceInfo = {
  id: string;
  number?: string | null;
  status?: string | null;
  amountDue?: number | null;
  amountPaid?: number | null;
  currency?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdf?: string | null;
  paymentIntentId?: string | null;
} | null;

type CheckoutTarget =
  | {
    planSlug: string;
    customerFeatureKey?: never;
  }
  | {
    customerFeatureKey: KnownCustomerFeatureKey;
    planSlug?: never;
  };

interface SubscriptionContextValue extends SubscriptionInfo {
  isLoading: boolean;
  refetch: (options?: CheckSubscriptionOptions) => Promise<SubscriptionInfo | null>;
  refreshStripeSubscription: () => Promise<SubscriptionInfo | null>;
  createCheckoutSession: (
    target: CheckoutTarget,
    billingInterval: BillingInterval,
    checkoutMode?: 'redirect' | 'custom',
  ) => Promise<CheckoutSessionInfo>;
  startCheckout: (
    planSlug: string,
    billingInterval: BillingInterval,
  ) => Promise<void>;
  startCustomerFeatureCheckout: (
    customerFeatureKey: KnownCustomerFeatureKey,
    billingInterval: BillingInterval,
  ) => Promise<void>;
  openBillingPortal: (options?: {
    flowType?: 'manage' | 'change_plan';
    targetPlanSlug?: string;
    targetBillingInterval?: BillingInterval;
  }) => Promise<void>;
  requestBillingChange?: (request: {
    requestType: 'cancel' | 'downgrade';
    targetPlanSlug?: string;
    targetBillingInterval?: BillingInterval;
    acknowledgement: boolean;
  }) => Promise<{
    requestId: string;
    status: string;
    feePennies: number;
    invoice: BillingChangeInvoiceInfo;
  }>;
  resumeScheduledCancellation: () => Promise<SubscriptionInfo | null>;
  requestRenewal: () => Promise<{
    requestId: string;
    status: 'created' | 'already_requested';
  }>;
  isGrandfathered: boolean;
  isManual: boolean;
  isStripe: boolean;
  needsSubscription: boolean;
}

interface CheckSubscriptionOptions {
  force?: boolean;
  silent?: boolean;
}

type CachedSubscription = {
  data: SubscriptionInfo;
  expiresAt: number;
};

type InFlightSubscriptionRequest = {
  organizationId: string;
  promise: Promise<SubscriptionInfo | null>;
};

const SUBSCRIPTION_CACHE_TTL_MS = 5 * 60_000;
const SUBSCRIPTION_BACKGROUND_REFRESH_MS = 15 * 60_000;

const subscriptionCache = new Map<string, CachedSubscription>();
const subscriptionInflight = new Map<string, Promise<SubscriptionInfo | null>>();

const initialSubscription: SubscriptionInfo = {
  subscribed: false,
  billingType: null,
  subscriptionStatus: null,
  planSlug: null,
  planName: null,
  billingInterval: null,
  subscriptionEnd: null,
  canceledAt: null,
  commitmentStartedAt: null,
  commitmentEndsAt: null,
};

function shouldTrustBootstrapSubscription(
  subscription: Pick<SubscriptionInfo, 'subscribed' | 'billingType' | 'subscriptionStatus'> | null,
) {
  if (!subscription) {
    return false;
  }

  if (
    subscription.billingType === 'stripe' &&
    !subscription.subscribed &&
    subscription.subscriptionStatus == null
  ) {
    return false;
  }

  return true;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(
  undefined,
);

function getBypassedSubscriptionState(): SubscriptionInfo {
  return {
    subscribed: true,
    billingType: 'manual',
    subscriptionStatus: 'active',
    planSlug: null,
    planName: null,
    billingInterval: null,
    subscriptionEnd: null,
    canceledAt: null,
  };
}

function getSubscriptionCacheKey(organizationId: string) {
  return organizationId;
}

export function resetSubscriptionCacheForTests() {
  subscriptionCache.clear();
  subscriptionInflight.clear();
}

function getInvokeResponseStatus(
  error: unknown,
  response?: Response,
): number | undefined {
  if (response) {
    return response.status;
  }

  if (
    (error instanceof FunctionsHttpError ||
      error instanceof FunctionsRelayError) &&
    error.context instanceof Response
  ) {
    return error.context.status;
  }

  return undefined;
}

function getFunctionInvokeContext(error: FunctionInvokeError) {
  return {
    functionName: error.functionName,
    status: error.status,
    responseBody: error.responseBody,
    responseJson: error.responseJson,
    errorCode: error.errorCode,
    errorClass: error.errorClass,
    rpcName: error.rpcName,
    requestId: error.requestId,
  };
}

function shouldCaptureCheckSubscriptionError(
  error: unknown,
  response?: Response,
): boolean {
  if (error instanceof FunctionsFetchError || error instanceof FunctionsRelayError) {
    return true;
  }

  if (error instanceof FunctionsHttpError) {
    const status = getInvokeResponseStatus(error, response);
    return status !== 401 && status !== 403;
  }

  return true;
}

function shouldReuseLastKnownSubscription(
  error: unknown,
  response?: Response,
): boolean {
  if (error instanceof FunctionsFetchError || error instanceof FunctionsRelayError) {
    return true;
  }

  if (error instanceof FunctionsHttpError) {
    const status = getInvokeResponseStatus(error, response);
    return status === undefined || status >= 500;
  }

  return true;
}

async function getFunctionErrorMessage(error: unknown): Promise<string> {
  if (
    (error instanceof FunctionsHttpError ||
      error instanceof FunctionsRelayError) &&
    error.context instanceof Response
  ) {
    try {
      const payload = await error.context.clone().json();
      if (payload && typeof payload.error === 'string') {
        return payload.error;
      }
    } catch {
      // Fall through to generic formatting.
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

function SubscriptionStateProvider({ children }: { children: ReactNode }) {
  const { organization, user, isMspContext, bootstrapSubscription } = useOrganization();
  const organizationId = organization?.id ?? null;
  const userId = user?.id ?? null;
  const isAwaitingOrganizationBootstrap =
    !isMspContext && !bootstrapSubscription && Boolean(userId) && !organizationId;
  const [subscription, setSubscription] = useState<SubscriptionInfo>(() => {
    if (isMspContext) {
      return getBypassedSubscriptionState();
    }

    if (shouldTrustBootstrapSubscription(bootstrapSubscription)) {
      return bootstrapSubscription;
    }

    if (!organizationId) {
      return initialSubscription;
    }

    const cached = subscriptionCache.get(getSubscriptionCacheKey(organizationId));
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    return initialSubscription;
  });
  const [isLoading, setIsLoading] = useState(() => {
    if (isMspContext) {
      return false;
    }

    if (shouldTrustBootstrapSubscription(bootstrapSubscription)) {
      return false;
    }

    if (!userId) {
      return false;
    }

    if (!organizationId) {
      return true;
    }

    const cached = subscriptionCache.get(getSubscriptionCacheKey(organizationId));
    return !(cached && cached.expiresAt > Date.now());
  });
  const lastCapturedErrorRef = useRef<{ key: string; at: number } | null>(null);
  const inFlightRef = useRef<InFlightSubscriptionRequest | null>(null);
  const latestOrganizationIdRef = useRef<string | null>(organizationId);
  const invokeWithStepUp = useStepUpInvoke();

  latestOrganizationIdRef.current = organizationId;

  useEffect(() => {
    if (!organizationId || !shouldTrustBootstrapSubscription(bootstrapSubscription)) {
      return;
    }

    const cacheKey = getSubscriptionCacheKey(organizationId);
    subscriptionCache.set(cacheKey, {
      data: bootstrapSubscription,
      expiresAt: Date.now() + SUBSCRIPTION_CACHE_TTL_MS,
    });
    setSubscription(bootstrapSubscription);
    setIsLoading(false);
  }, [bootstrapSubscription, organizationId]);

  useEffect(() => {
    if (!isAwaitingOrganizationBootstrap) {
      return;
    }

    setSubscription(initialSubscription);
    setIsLoading(true);
  }, [isAwaitingOrganizationBootstrap]);

  const captureSubscriptionError = useCallback(
    async (scope: string, error: Error) => {
      const sentryMessage = getFunctionInvokeSentryTitle(
        error,
        error.message || `Subscription ${scope} failed`,
      );
      const key = `${scope}:${sentryMessage}`;
      const now = Date.now();
      const last = lastCapturedErrorRef.current;

      if (last && last.key === key && now - last.at < 5 * 60 * 1000) {
        return;
      }

      const sentryContext = await getFunctionInvokeErrorSentryContext(error, {
        tags: {
          feature: 'subscription',
          scope,
          organization_id: organizationId,
        },
        extra: {
          subscription: {
            hasUser: Boolean(userId),
            hasOrganization: Boolean(organizationId),
            isMspContext,
          },
        },
      });
      const sentryFingerprint = await getFunctionInvokeErrorSentryFingerprint(error);
      Sentry.captureException(new Error(sentryMessage), {
        ...sentryContext,
        fingerprint: sentryFingerprint,
      });

      lastCapturedErrorRef.current = { key, at: now };
    },
    [isMspContext, organizationId, userId],
  );

  const checkSubscription = useCallback(
    async (
      { force = false, silent = false }: CheckSubscriptionOptions = {},
    ): Promise<SubscriptionInfo | null> => {
      if (isMspContext) {
        const bypassedState = getBypassedSubscriptionState();
        setSubscription(bypassedState);
        setIsLoading(false);
        return bypassedState;
      }

      if (!userId) {
        setSubscription(initialSubscription);
        setIsLoading(false);
        return null;
      }

      if (!organizationId) {
        setSubscription(initialSubscription);
        setIsLoading(true);
        return null;
      }

      const cacheKey = getSubscriptionCacheKey(organizationId);
      const applyFallbackSubscription = (
        error?: unknown,
        response?: Response,
      ) => {
        if (latestOrganizationIdRef.current !== organizationId) {
          return;
        }

        const cached = subscriptionCache.get(cacheKey);
        if (shouldReuseLastKnownSubscription(error, response)) {
          setSubscription(cached?.data ?? initialSubscription);
          return;
        }

        setSubscription(initialSubscription);
      };

      if (!force) {
        if (shouldTrustBootstrapSubscription(bootstrapSubscription)) {
          setSubscription(bootstrapSubscription);
          setIsLoading(false);
          return bootstrapSubscription;
        }

        const cached = subscriptionCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
          setSubscription(cached.data);
          setIsLoading(false);
          return cached.data;
        }

        const inflightRequest =
          subscriptionInflight.get(cacheKey) ??
          (inFlightRef.current?.organizationId === organizationId
            ? inFlightRef.current.promise
            : null);
        if (inflightRequest) {
          const data = await inflightRequest;
          if (data && latestOrganizationIdRef.current === organizationId) {
            setSubscription(data);
            setIsLoading(false);
          }
          return data;
        }
      }

      if (!silent) {
        setIsLoading(true);
      }

      const task = (async () => {
        try {
          const { data, error, response } = await supabase.functions.invoke(
            'check-subscription',
            {
              body: { organizationId },
            },
          );

          if (error) {
            if (isFunctionInvokeAbortError(error)) {
              applyFallbackSubscription(error, response);
              return null;
            }

            const shouldCaptureError = shouldCaptureCheckSubscriptionError(error, response);
            if (shouldCaptureError) {
              console.error('Error checking subscription:', error);
              const errorMessage = (await getFunctionErrorMessage(error)) || 'unknown error';
              const detailedSubscriptionError = await buildFunctionInvokeError(
                'check-subscription',
                error,
                `check-subscription invoke failed: ${errorMessage}`,
                response,
              );
              captureSubscriptionError(
                'check-subscription',
                new FunctionInvokeError(
                  `check-subscription invoke failed: ${errorMessage}`,
                  getFunctionInvokeContext(detailedSubscriptionError),
                ),
              );
            }

            applyFallbackSubscription(error, response);

            return null;
          }

          const nextSubscription: SubscriptionInfo = {
            subscribed: data.subscribed ?? false,
            billingType: data.billing_type ?? null,
            subscriptionStatus: data.subscription_status ?? null,
            planSlug: data.plan_slug ?? null,
            planName: data.plan_name ?? null,
            billingInterval: data.billing_interval ?? null,
            subscriptionEnd: data.subscription_end ?? null,
            canceledAt: data.canceled_at ?? null,
            commitmentStartedAt: data.commitment_started_at ?? null,
            commitmentEndsAt: data.commitment_ends_at ?? null,
          };

          subscriptionCache.set(cacheKey, {
            data: nextSubscription,
            expiresAt: Date.now() + SUBSCRIPTION_CACHE_TTL_MS,
          });

          if (latestOrganizationIdRef.current === organizationId) {
            setSubscription(nextSubscription);
          }

          return nextSubscription;
        } catch (error) {
          if (isFunctionInvokeAbortError(error)) {
            applyFallbackSubscription(error);
            return null;
          }

          console.error('Subscription check failed:', error);
          const message = error instanceof Error ? error.message : String(error);
          captureSubscriptionError(
            'check-subscription',
            new Error(`check-subscription threw: ${message}`),
          );

          applyFallbackSubscription(error);

          return null;
        } finally {
          if (latestOrganizationIdRef.current === organizationId) {
            setIsLoading(false);
          }
        }
      })();

      const promise = task.finally(() => {
        if (inFlightRef.current?.promise === promise) {
          inFlightRef.current = null;
        }
        if (subscriptionInflight.get(cacheKey) === promise) {
          subscriptionInflight.delete(cacheKey);
        }
      });

      inFlightRef.current = {
        organizationId,
        promise,
      };
      subscriptionInflight.set(cacheKey, promise);

      return promise;
    },
    [
      captureSubscriptionError,
      bootstrapSubscription,
      isMspContext,
      organizationId,
      userId,
    ],
  );

  useEffect(() => {
    void checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    if (isMspContext || !organizationId || !userId) {
      return;
    }

    const refreshIfVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      void checkSubscription({ force: true, silent: true });
    };

    const interval = window.setInterval(() => {
      refreshIfVisible();
    }, SUBSCRIPTION_BACKGROUND_REFRESH_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkSubscription({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSubscription, isMspContext, organizationId, userId]);

  useEffect(() => {
    if (isMspContext || !organizationId || !userId) {
      return;
    }

    const handleFocus = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      void checkSubscription({ silent: true });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkSubscription, isMspContext, organizationId, userId]);

  const createCheckoutSession = useCallback(
    async (
      target: CheckoutTarget,
      billingInterval: BillingInterval,
      checkoutMode: 'redirect' | 'custom' = 'redirect',
    ): Promise<CheckoutSessionInfo> => {
      if (!organizationId) {
        throw new Error('No organisation');
      }

      const { data, error } = await supabase.functions.invoke(
        'create-checkout',
        {
          body: { ...target, billingInterval, organizationId, checkoutMode },
        },
      );

      if (error) {
        const errorMessage = await getFunctionErrorMessage(error);
        const detailedCheckoutError = await buildFunctionInvokeError(
          'create-checkout',
          error,
          `create-checkout invoke failed: ${errorMessage || 'unknown error'}`,
        );
        const checkoutError = new FunctionInvokeError(
          `create-checkout invoke failed: ${errorMessage || 'unknown error'}`,
          getFunctionInvokeContext(detailedCheckoutError),
        );
        captureSubscriptionError(
          'create-checkout',
          checkoutError,
        );
        throw checkoutError;
      }

      subscriptionCache.delete(getSubscriptionCacheKey(organizationId));

      return {
        url: typeof data?.url === 'string' ? data.url : null,
        clientSecret: typeof data?.clientSecret === 'string' ? data.clientSecret : null,
        sessionId: typeof data?.sessionId === 'string' ? data.sessionId : null,
        checkoutMode: data?.checkoutMode === 'custom' ? 'custom' : 'redirect',
      };
    },
    [captureSubscriptionError, organizationId],
  );

  const startCheckout = useCallback(
    async (planSlug: string, billingInterval: BillingInterval) => {
      const session = await createCheckoutSession(
        { planSlug },
        billingInterval,
        'redirect',
      );

      if (session.url) {
        window.location.assign(session.url);
        return;
      }

      throw new Error('Stripe checkout did not return a redirect URL.');
    },
    [createCheckoutSession],
  );

  const startCustomerFeatureCheckout = useCallback(
    async (
      customerFeatureKey: KnownCustomerFeatureKey,
      billingInterval: BillingInterval,
    ) => {
      const session = await createCheckoutSession(
        { customerFeatureKey },
        billingInterval,
        'redirect',
      );

      if (session.url) {
        window.location.assign(session.url);
        return;
      }

      throw new Error('Stripe checkout did not return a redirect URL.');
    },
    [createCheckoutSession],
  );

  const openBillingPortal = useCallback(async (options?: {
    flowType?: 'manage' | 'change_plan';
    targetPlanSlug?: string;
    targetBillingInterval?: BillingInterval;
  }) => {
    if (!organizationId) {
      throw new Error('No organisation');
    }

    const requestBody = {
      organizationId,
      flowType: options?.flowType ?? 'manage',
      targetPlanSlug: options?.targetPlanSlug,
      targetBillingInterval: options?.targetBillingInterval,
    };

    let data: { url?: string | null } | null = null;
    let error: unknown = null;

    try {
      const response = await invokeWithStepUp(
        (body: typeof requestBody) =>
          supabase.functions.invoke('customer-portal', {
            body,
          }),
        {
          stepUpReason: requestBody.flowType === 'manage'
            ? 'Confirm billing portal access'
            : 'Confirm billing plan change',
          args: requestBody,
        },
      );
      data = response.data as { url?: string | null } | null;
      error = response.error;
    } catch (invokeError) {
      if (invokeError instanceof StepUpCancelledError) {
        return;
      }

      if (invokeError instanceof StepUpFactorMissingError) {
        throw new Error(invokeError.message);
      }

      throw invokeError;
    }

    if (error) {
      const errorMessage = await getFunctionErrorMessage(error);
      const detailedPortalError = await buildFunctionInvokeError(
        'customer-portal',
        error,
        `customer-portal invoke failed: ${errorMessage || 'unknown error'}`,
      );
      const portalError = new FunctionInvokeError(
        `customer-portal invoke failed: ${errorMessage || 'unknown error'}`,
        getFunctionInvokeContext(detailedPortalError),
      );
      captureSubscriptionError(
        'customer-portal',
        portalError,
      );
      throw portalError;
    }

    if (data?.url) {
      window.open(data.url, '_self');
    }
  }, [captureSubscriptionError, invokeWithStepUp, organizationId]);

  const requestBillingChange = useCallback(async (request: {
    requestType: 'cancel' | 'downgrade';
    targetPlanSlug?: string;
    targetBillingInterval?: BillingInterval;
    acknowledgement: boolean;
  }) => {
    if (!organizationId) {
      throw new Error('No organisation');
    }

    const { data, error } = await invokeWithStepUp(
      (body: {
        organizationId: string;
        requestType: 'cancel' | 'downgrade';
        targetPlanSlug?: string;
        targetBillingInterval?: BillingInterval;
        acknowledgement: boolean;
      }) =>
        supabase.functions.invoke('request-billing-change', {
          body,
        }),
      {
        stepUpReason: request.requestType === 'cancel'
          ? 'Confirm cancellation request'
          : 'Confirm downgrade request',
        args: {
          organizationId,
          requestType: request.requestType,
          targetPlanSlug: request.targetPlanSlug,
          targetBillingInterval: request.targetBillingInterval,
          acknowledgement: request.acknowledgement,
        },
      },
    );

    if (error) {
      const errorMessage = await getFunctionErrorMessage(error);
      const detailedChangeError = await buildFunctionInvokeError(
        'request-billing-change',
        error,
        `request-billing-change invoke failed: ${errorMessage || 'unknown error'}`,
      );
      const changeError = new FunctionInvokeError(
        `request-billing-change invoke failed: ${errorMessage || 'unknown error'}`,
        getFunctionInvokeContext(detailedChangeError),
      );
      captureSubscriptionError(
        'request-billing-change',
        changeError,
      );
      throw changeError;
    }

    return {
      requestId: data?.requestId as string,
      status: (data?.status ?? 'created') as string,
      feePennies: Number(data?.feePennies ?? 0),
      invoice: (data?.invoice ?? null) as BillingChangeInvoiceInfo,
    };
  }, [captureSubscriptionError, invokeWithStepUp, organizationId]);

  const resumeScheduledCancellation = useCallback(async () => {
    if (!organizationId) {
      throw new Error('No organisation');
    }

    const { error } = await supabase.functions.invoke('resume-stripe-subscription', {
      body: { organizationId },
    });

    if (error) {
      const errorMessage = await getFunctionErrorMessage(error);
      const detailedResumeError = await buildFunctionInvokeError(
        'resume-stripe-subscription',
        error,
        `resume-stripe-subscription invoke failed: ${errorMessage || 'unknown error'}`,
      );
      const resumeError = new FunctionInvokeError(
        `resume-stripe-subscription invoke failed: ${errorMessage || 'unknown error'}`,
        getFunctionInvokeContext(detailedResumeError),
      );
      captureSubscriptionError(
        'resume-stripe-subscription',
        resumeError,
      );
      throw resumeError;
    }

    subscriptionCache.delete(getSubscriptionCacheKey(organizationId));
    return checkSubscription({ force: true, silent: true });
  }, [captureSubscriptionError, checkSubscription, organizationId]);

  const refreshStripeSubscription = useCallback(async () => {
    if (!organizationId) {
      throw new Error('No organisation');
    }

    const { error } = await supabase.functions.invoke('refresh-stripe-subscription', {
      body: { organizationId },
    });

    if (error) {
      const errorMessage = await getFunctionErrorMessage(error);
      const detailedRefreshError = await buildFunctionInvokeError(
        'refresh-stripe-subscription',
        error,
        `refresh-stripe-subscription invoke failed: ${errorMessage || 'unknown error'}`,
      );
      const refreshError = new FunctionInvokeError(
        `refresh-stripe-subscription invoke failed: ${errorMessage || 'unknown error'}`,
        getFunctionInvokeContext(detailedRefreshError),
      );
      captureSubscriptionError(
        'refresh-stripe-subscription',
        refreshError,
      );
      throw refreshError;
    }

    subscriptionCache.delete(getSubscriptionCacheKey(organizationId));
    return checkSubscription({ force: true, silent: true });
  }, [captureSubscriptionError, checkSubscription, organizationId]);

  const requestRenewal = useCallback(async () => {
    if (!organizationId) {
      throw new Error('No organisation');
    }

    const { data, error } = await supabase.functions.invoke(
      'request-subscription-renewal',
      {
        body: { organizationId },
      },
    );

    if (error) {
      const errorMessage = await getFunctionErrorMessage(error);
      const detailedRenewalError = await buildFunctionInvokeError(
        'request-subscription-renewal',
        error,
        `request-subscription-renewal invoke failed: ${
          errorMessage || 'unknown error'
        }`,
      );
      const renewalError = new FunctionInvokeError(
        `request-subscription-renewal invoke failed: ${
          errorMessage || 'unknown error'
        }`,
        getFunctionInvokeContext(detailedRenewalError),
      );
      captureSubscriptionError(
        'request-subscription-renewal',
        renewalError,
      );
      throw renewalError;
    }

    subscriptionCache.delete(getSubscriptionCacheKey(organizationId));

    return {
      requestId: data?.requestId as string,
      status: (data?.status ?? 'created') as 'created' | 'already_requested',
    };
  }, [captureSubscriptionError, organizationId]);

  const refetch = useCallback(
    (options?: CheckSubscriptionOptions) =>
      checkSubscription({ force: true, ...(options ?? {}) }),
    [checkSubscription],
  );

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      ...subscription,
      isLoading,
      refetch,
      refreshStripeSubscription,
      createCheckoutSession,
      startCheckout,
      startCustomerFeatureCheckout,
      openBillingPortal,
      requestBillingChange,
      resumeScheduledCancellation,
      requestRenewal,
      isGrandfathered: subscription.billingType === 'grandfathered',
      isManual: subscription.billingType === 'manual',
      isStripe: subscription.billingType === 'stripe',
      needsSubscription: !subscription.subscribed && !isLoading,
    }),
    [
      createCheckoutSession,
      isLoading,
      openBillingPortal,
      requestBillingChange,
      refreshStripeSubscription,
      resumeScheduledCancellation,
      refetch,
      requestRenewal,
      startCheckout,
      startCustomerFeatureCheckout,
      subscription,
    ],
  );

  return createElement(SubscriptionContext.Provider, { value }, children);
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  return createElement(SubscriptionStateProvider, null, children);
}

export function useSubscription() {
  const subscription = useContext(SubscriptionContext);
  if (!subscription) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }

  return subscription;
}
